import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Loader2, AlertTriangle, CreditCard, FileSignature, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

const PAYPAL_GIVING_CLIENT_ID = process.env.REACT_APP_PAYPAL_GIVING_CLIENT_ID;

export default function RentalDepositPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [paymentType, setPaymentType] = useState(null);
  const [needsSignature, setNeedsSignature] = useState(false);
  const [contractToken, setContractToken] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [selectedOption, setSelectedOption] = useState("full"); // "full" or "booking_fee"
  const paypalRef = useRef(null);
  const paypalRenderedRef = useRef(null); // track which option was rendered

  useEffect(() => {
    axios.get(`${API}/rentals/deposit/${token}`)
      .then((r) => {
        if (r.data.status === "deposit_paid" || r.data.status === "signed") {
          setConfirmed(true);
          setPaymentType("booking_fee");
        } else if (r.data.status === "full_paid") {
          setConfirmed(true);
          setPaymentType("full");
        } else if (r.data.status === "awaiting_signature") {
          setNeedsSignature(true);
          setContractToken(r.data.contract_token || "");
        } else {
          setInfo(r.data);
          // Default to full if rental_amount is available
          setSelectedOption(r.data.rental_amount > 0 ? "full" : "booking_fee");
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Payment link not found or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const getAmount = useCallback(() => {
    if (selectedOption === "full" && info?.rental_amount > 0) return info.rental_amount;
    return 50;
  }, [selectedOption, info]);

  const renderPayPalButtons = useCallback(() => {
    if (!window.paypal || !paypalRef.current) return;
    // Clear previous buttons
    paypalRef.current.innerHTML = "";
    const amount = getAmount();
    try {
      window.paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 },
        createOrder: (_data, actions) => actions.order.create({
          purchase_units: [{
            description: `WAGC Community Room: ${info.event_name || 'Rental'} (${info.event_date || ''})`.slice(0, 127),
            custom_id: `rental:${info.event_name || 'unknown'}:${info.email || 'unknown'}`.slice(0, 127),
            invoice_id: `wagc-rental-${token || 'unknown'}-${Date.now()}`.slice(0, 127),
            amount: { value: amount.toFixed(2) },
          }],
        }),
        onApprove: async (_data, actions) => {
          setProcessing(true);
          try {
            const details = await actions.order.capture();
            const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id || "";
            await axios.post(`${API}/rentals/deposit/${token}/pay`, {
              paypal_order_id: details.id,
              amount: amount,
              capture_id: captureId,
            });
            setConfirmed(true);
            setPaymentType(selectedOption === "full" ? "full" : "booking_fee");
            toast.success("Payment confirmed! Check your email for WiFi and key details.");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment failed. Please try again.");
          } finally {
            setProcessing(false);
          }
        },
        onError: () => toast.error("PayPal error — please try again."),
      }).render(paypalRef.current).then(() => {
        setPaypalReady(true);
        paypalRenderedRef.current = selectedOption;
      });
    } catch { /* PayPal not loaded */ }
  }, [token, getAmount, selectedOption, info?.event_name, info?.event_date, info?.email]);

  // Load PayPal SDK script
  useEffect(() => {
    if (window.paypal) { setSdkLoaded(true); return; }
    if (document.getElementById("paypal-rental-sdk")) return;
    const script = document.createElement("script");
    script.id = "paypal-rental-sdk";
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_GIVING_CLIENT_ID}&currency=USD`;
    script.onload = () => setSdkLoaded(true);
    document.body.appendChild(script);
  }, []);

  // Render PayPal buttons when SDK AND info are both ready, or option changes
  useEffect(() => {
    if (sdkLoaded && info && paypalRenderedRef.current !== selectedOption) {
      const timer = setTimeout(renderPayPalButtons, 200);
      return () => clearTimeout(timer);
    }
  }, [sdkLoaded, info, renderPayPalButtons, selectedOption]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="font-heading text-xl text-navy mb-2">Payment Link Invalid</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="deposit-page">
      <div className="bg-navy py-8 text-center">
        <h1 className="font-heading text-2xl sm:text-3xl text-white">Word Alive Global Center</h1>
        <p className="text-gold text-sm mt-1">Community Room Rental Payment</p>
      </div>

      <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
        {needsSignature ? (
          <div className="text-center py-10" data-testid="deposit-needs-signature">
            <FileSignature className="h-16 w-16 text-gold mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-navy mb-3">Sign Your Contract First</h2>
            <p className="text-gray-500 text-sm mb-6">Please sign the rental agreement before paying.</p>
            {contractToken && (
              <a
                href={`/rental/contract/${contractToken}`}
                className="inline-block bg-gold text-navy font-bold py-3 px-8 rounded text-base uppercase tracking-wider hover:bg-gold/90 transition-colors no-underline"
                data-testid="go-sign-contract-link"
              >
                Sign Contract Now
              </a>
            )}
          </div>
        ) : confirmed ? (
          <div className="text-center py-10" data-testid="deposit-confirmed">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-navy mb-2">Booking Confirmed!</h2>
            {paymentType === "full" ? (
              <p className="text-gray-500 text-sm">Full rental paid. Nothing due on arrival. Check your email for WiFi and key pickup details.</p>
            ) : (
              <p className="text-gray-500 text-sm">$50 booking fee received. Your date is booked. The remaining balance is due before your event, and we'll email you a reminder with a payment link. Check your email for WiFi and key pickup details.</p>
            )}
          </div>
        ) : info ? (
          <div>
            {/* Event summary */}
            <div className="bg-white rounded-sm border border-bone-dark p-5 mb-6">
              <h3 className="font-heading text-base text-navy mb-3">Booking Summary</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-gray-500">Event</span>
                <span className="text-navy font-medium">{info.event_name}</span>
                <span className="text-gray-500">Date</span>
                <span className="text-navy">{info.event_date} {info.event_time && `at ${info.event_time}`}</span>
                <span className="text-gray-500">Guests</span>
                <span className="text-navy">{info.guests}</span>
                {info.rental_amount > 0 && (
                  <>
                    <span className="text-gray-500">Total Rental</span>
                    <span className="text-navy font-heading text-lg">${info.rental_amount}</span>
                  </>
                )}
              </div>
            </div>

            {/* Payment options */}
            <div className="space-y-3 mb-6">
              <p className="font-heading text-base text-navy">Choose Your Payment Option</p>

              {/* Full payment - primary */}
              {info.rental_amount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedOption("full")}
                  className={`w-full text-left rounded-sm border-2 p-4 transition-all ${
                    selectedOption === "full"
                      ? "border-gold bg-gold/5 shadow-sm"
                      : "border-bone-dark bg-white hover:border-gold/40"
                  }`}
                  data-testid="option-full-pay"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center mt-0.5 ${
                      selectedOption === "full" ? "border-gold" : "border-gray-300"
                    }`}>
                      {selectedOption === "full" && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`font-heading text-base ${selectedOption === "full" ? "text-navy" : "text-gray-600"}`}>
                          Pay Full Rental &mdash; ${info.rental_amount}
                        </p>
                        <span className="bg-gold/20 text-gold text-[10px] font-bold uppercase px-1.5 py-0.5 rounded tracking-wider flex items-center gap-1">
                          <Star className="h-2.5 w-2.5" /> Best Value
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1">Includes $50 booking fee. Nothing due on arrival.</p>
                    </div>
                  </div>
                </button>
              )}

              {/* $50 booking fee - secondary */}
              <button
                type="button"
                onClick={() => setSelectedOption("booking_fee")}
                className={`w-full text-left rounded-sm border-2 p-4 transition-all ${
                  selectedOption === "booking_fee"
                    ? "border-gold bg-gold/5 shadow-sm"
                    : "border-bone-dark bg-white hover:border-gold/40"
                }`}
                data-testid="option-booking-fee"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center mt-0.5 ${
                    selectedOption === "booking_fee" ? "border-gold" : "border-gray-300"
                  }`}>
                    {selectedOption === "booking_fee" && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                  </div>
                  <div className="flex-1">
                    <p className={`font-heading text-base ${selectedOption === "booking_fee" ? "text-navy" : "text-gray-600"}`}>
                      $50 Non-Refundable Booking Fee Only
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      Secures your date. {info.rental_amount > 0
                        ? `Remaining $${info.rental_amount - 50} balance is due before your event. Pay it at The Master's Blend or online with a link we'll email you.`
                        : "Remaining balance is due before your event."}
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* PayPal section */}
            <div className="bg-white rounded-sm border border-bone-dark p-6 mb-6" data-testid="deposit-paypal-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-gold" />
                  <h3 className="font-heading text-lg text-navy">
                    Pay ${getAmount()} via PayPal / Card
                  </h3>
                </div>
              </div>
              {processing ? (
                <div className="flex items-center justify-center gap-2 py-4 text-navy">
                  <Loader2 className="h-5 w-5 animate-spin text-gold" />
                  <span className="text-sm">Processing payment...</span>
                </div>
              ) : (
                <div ref={paypalRef} className="min-h-[50px]" />
              )}
              {!paypalReady && !processing && (
                <p className="text-xs text-gray-400 text-center py-2">Loading payment options...</p>
              )}
            </div>
          </div>
        ) : null}

        <p className="text-center text-xs text-gray-400 mt-6">1128 W Panola Street, Carthage, TX 75633</p>
      </div>
    </div>
  );
}
